import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useDispatch } from 'react-redux';
import { createRequestTypes, INVITE_FAVORITE_TO_JOB } from '~/actions';
import { apiMethods } from '~/services/api';
import { apiMethodsConst } from '~/services/methods';
import { IFavoriteInvite, IJob } from '~/models';
import { jobKeys } from '../jobKeys';

interface IInviteFavoriteToJobPayload {
  jobId: string;
  userIds: string[];
  officeId?: string;
}

const inviteFavoriteEvents = createRequestTypes(INVITE_FAVORITE_TO_JOB) as {
  REQUEST: string;
  SUCCESS: string;
  FAILURE: string;
};

export function useInviteFavoriteToJobMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, IInviteFavoriteToJobPayload>({
    mutationFn: async ({ jobId, userIds, officeId }) => {
      await apiMethods[apiMethodsConst.INVITE_FAVORITE_TO_JOB_REQUEST]({
        jobId,
        userIds,
        officeId,
      });
    },
    onMutate: () => {
      dispatch({ type: inviteFavoriteEvents.REQUEST });
    },
    onSuccess: (_data, payload) => {
      // Preserve the legacy reducer UX: invited rows flip to "Sent" immediately,
      // then the detail query refetch below reconciles with the server.
      queryClient.setQueryData<IJob>(jobKeys.detail(payload.jobId), (old) => {
        if (!old) return old;

        const existingInvites = old.favoriteInvites ?? [];
        const existingProfessionalIds = new Set(
          existingInvites.map((invite) => invite?.professional as unknown as string),
        );
        const newInvites = payload.userIds
          .filter((id) => !existingProfessionalIds.has(id))
          .map((id) => ({ professional: id }) as unknown as IFavoriteInvite);

        return {
          ...old,
          favoriteInvites: [...existingInvites, ...newInvites],
        };
      });
      dispatch({ type: inviteFavoriteEvents.SUCCESS });
      queryClient.invalidateQueries({ queryKey: jobKeys.detail(payload.jobId) });
    },
    onError: (error) => {
      dispatch({
        type: inviteFavoriteEvents.FAILURE,
        payload: error.response?.data ?? error,
      });
    },
  });
}
